import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { Bell, Megaphone } from "lucide-react";
import { slurpApi } from "../../lib/slurpApi";

/**
 * Notifications bell mounted in the TopBar.
 *
 *   • Polls the connected wallet's notifications every 60s.
 *   • Unread count = rows where `read_at` is null (server-side, so the
 *     read state survives logout / different browser / different device).
 *   • Click bell → fetch fresh + POST mark-all-read so the badge clears
 *     for every future session everywhere.
 *
 * If no wallet is connected, the bell stays hidden.
 */
const POLL_MS = 60 * 1000;

function timeAgo(ms) {
  const s = Math.max(1, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

const shortAddr = (a) => (!a ? "—" : `${a.slice(0, 4)}…${a.slice(-4)}`);

export default function NotificationsBell() {
  const wallet = useWallet();
  const navigate = useNavigate();
  const walletStr = wallet.publicKey?.toBase58() || null;

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // IDs we've locally marked read this session — guards against the
  // 60s poll re-fetching items that we just marked read but the server
  // hasn't yet returned with read_at set.
  const [locallyRead, setLocallyRead] = useState(() => new Set());
  const dropRef = useRef(null);
  const btnRef = useRef(null);

  const fetchItems = useCallback(async () => {
    if (!walletStr) return;
    setLoading(true);
    try {
      const r = await slurpApi.getNotifications(walletStr, { limit: 30 });
      setItems(Array.isArray(r?.notifications) ? r.notifications : []);
    } catch {
      /* swallow — bell badge is non-critical */
    } finally {
      setLoading(false);
    }
  }, [walletStr]);

  useEffect(() => {
    if (!walletStr) { setItems([]); return; }
    fetchItems();
    const id = setInterval(fetchItems, POLL_MS);
    return () => clearInterval(id);
  }, [walletStr, fetchItems]);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (dropRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Per-wallet mute toggle. Stored in localStorage so it survives reloads
  // but stays per-device (a future v2 could move this server-side via the
  // profile if users want it to follow them around). Declared BEFORE
  // `unread` because `unread` reads `muted` and a TDZ would crash render.
  const muteKey = walletStr ? `slurp:notifs_muted:${walletStr}` : null;
  const [muted, setMuted] = useState(() => {
    if (!muteKey) return false;
    try { return window.localStorage.getItem(muteKey) === "1"; } catch { return false; }
  });
  useEffect(() => {
    if (!muteKey) return;
    try {
      setMuted(window.localStorage.getItem(muteKey) === "1");
    } catch { /* noop */ }
  }, [muteKey]);
  const toggleMute = () => {
    if (!muteKey) return;
    const next = !muted;
    setMuted(next);
    try { window.localStorage.setItem(muteKey, next ? "1" : "0"); } catch { /* noop */ }
  };

  // Broadcast read tracking. Broadcasts have recipient='*' on the server
  // (one row to many wallets), so the per-row read_at column doesn't fit.
  // Track per-device in localStorage instead — broadcasts are infrequent
  // and the UX cost of re-reading on a different device is acceptable.
  const broadcastReadsKey = walletStr ? `slurp:broadcast_reads:${walletStr}` : null;
  const [broadcastReads, setBroadcastReads] = useState(() => {
    if (!broadcastReadsKey) return new Set();
    try {
      const raw = window.localStorage.getItem(broadcastReadsKey);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
  });
  // Re-hydrate from localStorage when the wallet changes. The lazy
  // initializer above only runs once, so without this a wallet that
  // connects AFTER mount would start with an empty Set — the badge would
  // light back up for already-read broadcasts on every reload.
  useEffect(() => {
    if (!broadcastReadsKey) { setBroadcastReads(new Set()); return; }
    try {
      const raw = window.localStorage.getItem(broadcastReadsKey);
      const arr = raw ? JSON.parse(raw) : [];
      setBroadcastReads(new Set(Array.isArray(arr) ? arr : []));
    } catch { setBroadcastReads(new Set()); }
  }, [broadcastReadsKey]);
  const persistBroadcastReads = useCallback((set) => {
    if (!broadcastReadsKey) return;
    try { window.localStorage.setItem(broadcastReadsKey, JSON.stringify(Array.from(set))); }
    catch { /* noop */ }
  }, [broadcastReadsKey]);

  // Treat a row as unread if either the server `read_at` (targeted rows)
  // OR the localStorage broadcast-read set (broadcast rows) says so.
  const isUnread = useCallback((n) => {
    if (locallyRead.has(n.id)) return false;
    if (n.type === "broadcast") return !broadcastReads.has(n.id);
    return !n.read_at;
  }, [locallyRead, broadcastReads]);

  const unread = useMemo(
    () => (muted ? 0 : items.filter(isUnread).length),
    [items, muted, isUnread]
  );

  const onToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && walletStr) {
      // Snapshot every currently-unread id into the local "read" set so
      // the next poll can't bring the badge back to non-zero on items
      // the user has already seen.
      setLocallyRead((prev) => {
        const s = new Set(prev);
        for (const n of items) if (isUnread(n)) s.add(n.id);
        return s;
      });
      // Persist broadcast reads so they don't pop back to unread on
      // reload. Targeted rows are still synced via server-side read_at.
      const newBroadcastReads = new Set(broadcastReads);
      let added = false;
      for (const n of items) {
        if (n.type === "broadcast" && !newBroadcastReads.has(n.id)) {
          newBroadcastReads.add(n.id);
          added = true;
        }
      }
      if (added) {
        setBroadcastReads(newBroadcastReads);
        persistBroadcastReads(newBroadcastReads);
      }
      slurpApi.markNotificationsRead(walletStr).catch(() => { /* non-critical */ });
      setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })));
      fetchItems();
    }
  };

  if (!walletStr) return null;

  return (
    <div className="pm-bell">
      <button
        ref={btnRef}
        type="button"
        className="pm-bell__btn"
        onClick={onToggle}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="pm-bell__badge">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>

      {open && (
        <div ref={dropRef} className="pm-bell__drop" role="menu">
          <div className="pm-bell__head">
            <span className="pm-bell__head-title">
              Notifications
              {loading && <span className="pm-bell__spinner" aria-label="Loading" />}
            </span>
            <button
              type="button"
              className={`pm-bell__mute${muted ? " is-muted" : ""}`}
              onClick={toggleMute}
              title={muted ? "Unmute notifications" : "Mute all notifications"}
            >
              {muted ? "muted" : "mute"}
            </button>
          </div>
          {muted ? (
            <div className="pm-bell__empty">
              Notifications are muted. Tap "muted" above to turn them back on.
            </div>
          ) : items.length === 0 ? (
            <div className="pm-bell__empty">
              {loading ? "Loading…" : "Nothing yet."}
            </div>
          ) : (
            <ul className="pm-bell__list">
              {items.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  onJump={() => {
                    setOpen(false);
                    const data = n.data || {};
                    if (n.type === "broadcast") {
                      // Admin-pushed announcement. Open the link if one
                      // was attached, otherwise the row click is just a
                      // dismiss action (the toggle already marked it read).
                      if (typeof data.link === "string" && /^https?:\/\//i.test(data.link)) {
                        window.open(data.link, "_blank", "noopener,noreferrer");
                      }
                      return;
                    }
                    if (n.type === "launch" && n.mint) {
                      navigate(`/token/${n.mint}`);
                    } else if (
                      (n.type === "group_thread_reply" || n.type === "group_comment_reply" || n.type === "group_flag")
                      && n.mint && data.thread_id
                    ) {
                      navigate(`/groups/${n.mint}/thread/${data.thread_id}`);
                    } else if (n.actor) {
                      navigate(`/profile/${n.actor}`);
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationRow({ n, onJump }) {
  const data = n.data || {};
  const actorName = n.actor_username ? `@${n.actor_username}` : shortAddr(n.actor);
  const tsMs = new Date(n.created_at).getTime();
  const isBroadcast = n.type === "broadcast";
  const renderTitle = () => {
    switch (n.type) {
      case "broadcast":
        return <b>{data.title || "Slurp announcement"}</b>;
      case "launch":
        return <><b>{actorName}</b> launched <b>${data.symbol || "?"}</b></>;
      case "group_thread_reply":
        return <><b>{actorName}</b> replied to your thread</>;
      case "group_comment_reply":
        return <><b>{actorName}</b> replied to your comment</>;
      case "group_flag":
        return <><b>{actorName}</b> reported a {data.target_kind || "post"}{data.auto_hidden ? " (auto-hidden)" : ""}</>;
      default:
        return <><b>{actorName}</b> · {n.type}</>;
    }
  };
  const renderSub = () => {
    if (n.type === "broadcast") return data.body || null;
    if (n.type === "launch" && data.name) return data.name;
    if ((n.type === "group_thread_reply" || n.type === "group_comment_reply") && data.snippet) {
      return data.snippet;
    }
    return null;
  };
  const sub = renderSub();
  return (
    <li>
      <button
        type="button"
        className={`pm-bell__row${isBroadcast ? " pm-bell__row--broadcast" : ""}`}
        onClick={onJump}
      >
        <span className="pm-bell__row-avatar">
          {isBroadcast ? (
            <span className="pm-bell__row-avatar--broadcast"><Megaphone size={16} /></span>
          ) : n.actor_image_url ? (
            <img src={n.actor_image_url} alt="" />
          ) : (
            <img src="/images/png/slurpface (1).png" alt="" className="pm-bell__row-avatar--default" />
          )}
        </span>
        <span className="pm-bell__row-body">
          <span className="pm-bell__row-title">{renderTitle()}</span>
          {sub && <span className="pm-bell__row-sub">{sub}</span>}
        </span>
        <span className="pm-bell__row-time">{timeAgo(tsMs)}</span>
      </button>
    </li>
  );
}
